import React, { Suspense, useEffect, useState } from "react";

import * as API from "@ekonsilio/api-livechat";
import { decode } from "html-entities";
import omit from "lodash/omit";
import { deviceType } from "react-device-detect";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";

import TabNotification from "./TabNotifications";

import { useBehaviors } from "../utils/behaviors";
import { useConversationMessagesQuery } from "../utils/queries/conversations";
import { useSDK } from "../utils/sdk";
import { useWebmessenger } from "../utils/webmessenger";
import { useWebSocket } from "../utils/websocket";

const Button = React.lazy(() => import("./Button"));
const Window = React.lazy(() => import("./window"));
const NotificationComponent = React.lazy(() => import("./Notification"));

const webmessengerApi = new API.Messaging.Webmessenger();

function App() {
    const {
        maximized,
        openWindow,
        closeWindow,
        hidden,
        hide,
        show,
        notification,
        createNotification,
        closeNotification,
        conversation,
        setConversation,
        setOperator,
        operator,
        integration,
        online,
        setOnline,
        unreadMessagesCount,
        incrementUnreadMessagesCount,
        resetUnreadMessagesCount,
    } = useWebmessenger();

    const intl = useIntl();

    const {
        disable,
        hideChat,
        triggerOpenWindowBehaviors,
        isInit: behaviorsIsInit,
    } = useBehaviors();
    const { websocket, connected } = useWebSocket();

    const [newMessage, setNewMessage] = useState("");
    const [allowSendNotification, setAllowSendNotification] = useState(true);
    const [conversationNotification, setConversationNotification] =
        useState(true);
    const [soundNotification, setSoundNotification] = useState(false);

    const getSessionStorage = () => {
        const sessionData = sessionStorage.getItem("eK-behaviors");
        return sessionData ? JSON.parse(sessionData) : null;
    };

    const storage = getSessionStorage();

    const { emit } = useSDK();

    const { isSuccess: operatorStatusIsSuccess } = useQuery(
        ["operator", "status"],
        async () =>
            await webmessengerApi.getOperator(
                integration,
                operator?._id,
                operator?.avatar
            ),
        {
            onSuccess: async (data) => {
                if (process.env.REACT_APP_ANALYTICS) {
                    try {
                        if (!hideChat) {
                            const body = {
                                fingerprint: window.eK.fingerprint,
                                integration: window.eK.integration,
                                entity: window.eK.entity,
                                url: window.location.href,
                                online: !data.offline,
                                root_cause: data.rootCause,
                            };

                            await fetch(
                                `${process.env.REACT_APP_ANALYTICS}/status`,
                                {
                                    method: "POST",
                                    headers: {
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify(body),
                                }
                            );
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }

                if (data.offline) {
                    if (!conversation) {
                        disable();
                        setOnline(false);
                        hide();
                    } else {
                        show();
                    }
                } else if (!hideChat) {
                    setOnline(true);
                    if (hidden) show();
                }
            },
            enabled: integration !== null,
            refetchOnWindowFocus: true,
            staleTime: 10000,
        }
    );

    useEffect(() => {
        if (storage && "notificationConversation" in storage.triggered) {
            if (!storage.triggered.notificationConversation) {
                setConversationNotification(false);
            } else {
                setConversationNotification(true);
            }
        }
    }, [storage]);

    useConversationMessagesQuery(conversation?._id, true, {
        onSuccess: ({ data }) => {
            const messages = data.filter((m) => m.author?.type === "operator");

            if (messages.length > 0 && conversationNotification) {
                createNotification({
                    title: intl.formatMessage({
                        id: "WEBMESSENGER.NOTIFICATION.NEW_MESSAGE",
                        defaultMessage: "Nouveau message",
                    }),
                    text: messages[messages.length - 1].content,
                });
                incrementUnreadMessagesCount(messages.length);

                for (const message of messages) {
                    webmessengerApi
                        .markMessageAsReceived(conversation._id, message._id)
                        .catch((err) => err);
                }
            }
        },
        enabled:
            conversation !== null &&
            conversation.status !== "closed" &&
            !maximized &&
            connected,
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    useEffect(() => {
        if (websocket && connected) {
            const onNewMessage = (message) => {
                const authorType = message.author?.type;
                if (authorType === "visitor") setAllowSendNotification(true);

                if (
                    (authorType === "operator" ||
                        authorType === "agent" ||
                        message.type === "system" &&
                        message.content?.type !== "leadSent") &&
                    conversation &&
                    conversation._id === message.conversation
                ) {
                    if (
                        soundNotification &&
                        deviceType === "browser" &&
                        message.type !== "system"
                    ) {
                        const soundElt = document.getElementById("eK-sound");
                        if (soundElt) soundElt.play();
                    }

                    if (document.hidden) {
                        setNewMessage(message.content);
                        if (allowSendNotification) {
                            const newNotification = () =>
                                new Notification(
                                    intl.formatMessage({
                                        id: "WEBMESSENGER.NOTIFICATION.CONVERSATION_TRANSFERRED.TITLE",
                                        defaultMessage: "Nouveau message",
                                    }),
                                    {
                                        body:
                                            message.content?.type === "transfer"
                                                ? intl.formatMessage({
                                                      id: "WEBMESSENGER.NOTIFICATION.CONVERSATION_TRANSFERRED.TEXT",
                                                      defaultMessage:
                                                          "La conversation a été transférée à un autre conseiller",
                                                  })
                                                : decode(message.content),
                                        icon: `${window.location.origin}/favicon.ico`,
                                        badge: "faviconNotification.png",
                                    }
                                );
                            newNotification();
                            setAllowSendNotification(false);
                            setTimeout(
                                () => setAllowSendNotification(true),
                                20000
                            );
                        }
                    }

                    if (!maximized && message.type !== "system") {
                        createNotification({
                            title: intl.formatMessage({
                                id: "WEBMESSENGER.NOTIFICATION.NEW_MESSAGE",
                                defaultMessage: "Nouveau message",
                            }),
                            text: message.content,
                        });
                        setNewMessage(message.content);
                        incrementUnreadMessagesCount();
                        webmessengerApi
                            .markMessageAsReceived(
                                conversation._id,
                                message._id
                            )
                            .catch((err) => err);

                        storage.triggered = {
                            ...storage.triggered,
                            notificationConversation: true,
                        };

                        sessionStorage.setItem(
                            "eK-behaviors",
                            JSON.stringify(storage)
                        );
                    }
                }
            };

            websocket.on("message:new", onNewMessage);

            const onConversationClosed = ({ _id, events }) => {
                /**
                 * Only set the status if there is a conversation
                 */
                if (conversation && conversation._id === _id) {
                    setConversation((previousState) => ({
                        ...previousState,
                        status: "closed",
                        events,
                    }));
                }
            };

            websocket.on("conversation:closed", onConversationClosed);

            const onConversationTransfer = ({
                conversation: conversationTransfer,
                to,
            }) => {
                setOperator({
                    ...omit(
                        conversationTransfer.participants.operators.find(
                            ({ _id, current }) => _id === to && current
                        ),
                        ["current", "startedAt", "endedAt"]
                    ),
                });
            };

            websocket.on("conversation:transfer", onConversationTransfer);

            return () => {
                websocket.removeListener("message:new", onNewMessage);
                websocket.removeListener(
                    "conversation:closed",
                    onConversationClosed
                );
                websocket.removeListener(
                    "conversation:transfer",
                    onConversationTransfer
                );
            };
        }
        return () => {};
    }, [
        websocket,
        connected,
        maximized,
        createNotification,
        incrementUnreadMessagesCount,
        resetUnreadMessagesCount,
        conversation,
        closeWindow,
        setConversation,
        setOperator,
        allowSendNotification,
        intl,
        soundNotification,
    ]);

    useEffect(() => {
        emit("service:status", {
            online,
            operator:
                online && operator?.name && operator?.title && operator?.face
                    ? {
                          name: operator.name,
                          title: operator.title,
                          picture: `${process.env.REACT_APP_FILE}/files/avatars/${operator.face}`,
                      }
                    : null,
        });
    }, [online, operator]);

    // Send ChatView event when hidden is false
    useEffect(() => {
        let timeout = null;

        if (process.env.REACT_APP_ANALYTICS) {
            timeout = setTimeout(() => {
                if (!hidden && behaviorsIsInit && operatorStatusIsSuccess) {
                    const body = {
                        fingerprint: window.eK.fingerprint,
                        integration: window.eK.integration,
                        entity: window.eK.entity,
                        url: window.location.href,
                        deviceType,
                    };

                    fetch(`${process.env.REACT_APP_ANALYTICS}/chatView`, {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                    });
                }
            }, 1000);
        }

        return () => {
            if (timeout !== null) clearTimeout(timeout);
        };
    }, [hidden, operatorStatusIsSuccess, behaviorsIsInit]);

    useEffect(() => {
        const sessionValue = sessionStorage.getItem("eK-sound-notification");
        let isSoundNotificationEnabled = false;

        if (sessionValue === null) {
            const initialSound = window.eK.sound !== "none";
            isSoundNotificationEnabled = initialSound;
        } else {
            isSoundNotificationEnabled = sessionValue === "true";
        }

        setSoundNotification(isSoundNotificationEnabled);

        if (
            isSoundNotificationEnabled &&
            !document.getElementById("eK-sound")
        ) {
            const audioElement = document.createElement("audio");

            audioElement.id = "eK-sound";
            audioElement.src = `${process.env.PUBLIC_URL}/sounds/${window.eK.sound}.wav`;
            audioElement.autoplay = true;
            audioElement.preload = "auto";

            document.body.appendChild(audioElement);

            const trackElement = document.createElement("track");
            trackElement.kind = "captions";

            audioElement.appendChild(trackElement);
            audioElement.load();
            audioElement.play();
            audioElement.pause();
        }
    }, []);

    const onToggleSoundNotification = (toggleSound) => {
        sessionStorage.setItem("eK-sound-notification", toggleSound);
        setSoundNotification(toggleSound);
    };

    return (
        <>
            <TabNotification
                newMessage={newMessage}
                onResetMessage={() => setNewMessage("")}
            />
            {maximized ? (
                <Suspense fallback={<span />}>
                    <Window
                        onMinimze={closeWindow}
                        soundNotification={soundNotification}
                        onToggleSoundNotification={() =>
                            onToggleSoundNotification(!soundNotification)
                        }
                    />
                </Suspense>
            ) : (
                !hidden && (
                    <>
                        <Suspense fallback={<span />}>
                            <Button
                                onClick={() => {
                                    if (notification) {
                                        closeNotification();
                                    }
                                    if (unreadMessagesCount) {
                                        resetUnreadMessagesCount();
                                    }
                                    openWindow(true);
                                    triggerOpenWindowBehaviors();

                                    emit("window:maximized", {
                                        conversation: conversation?._id || null,
                                    });
                                }}
                            />
                        </Suspense>
                        <Suspense fallback={<span />}>
                            {notification && (
                                <NotificationComponent
                                    onClick={() => {
                                        if (notification) {
                                            closeNotification();
                                        }
                                        if (unreadMessagesCount) {
                                            resetUnreadMessagesCount();
                                        }
                                        openWindow(true);
                                        triggerOpenWindowBehaviors();
                                    }}
                                />
                            )}
                        </Suspense>
                    </>
                )
            )}
        </>
    );
}

export default App;
